import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { UserService } from '@/services/telehealthApi/UserService'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import type { AssignableUserDTO } from '@/generated'

export const useAccessibleUsersStore = defineStore('accessibleUsers', () => {
  const users = ref<AssignableUserDTO[]>([])
  const isLoadingUsers = ref(false)

  const usersByUserName = computed(() => {
    const userMap = new Map<string, AssignableUserDTO>()
    users.value.forEach((user) => {
      userMap.set(user.userName, user)
    })
    return userMap
  })

  async function refreshUsers(): Promise<void> {
    isLoadingUsers.value = true
    try {
      const response = await UserService.getAvailableUsers()
      users.value = response
    } catch (error) {
      useNoticeStore().add({
        type: NoticeType.ERROR,
        message: 'Could not load users'
      })
    } finally {
      isLoadingUsers.value = false
    }
  }

  function getUserByUserName(userName: string): AssignableUserDTO | undefined {
    return usersByUserName.value.get(userName)
  }

  function updateUser(userName: string, updatedUser: Partial<AssignableUserDTO>): void {
    const index = users.value.findIndex((user) => user.userName === userName)
    if (index !== -1) {
      users.value[index] = { ...users.value[index], ...updatedUser }
    }
  }

  function addUser(user: AssignableUserDTO): void {
    const existingIndex = users.value.findIndex((existing) => existing.userName === user.userName)

    if (existingIndex !== -1) {
      users.value[existingIndex] = user
    } else {
      users.value.push(user)
    }
  }

  function removeUser(userName: string): void {
    const index = users.value.findIndex((user) => user.userName === userName)
    if (index !== -1) {
      users.value.splice(index, 1)
    }
  }

  return {
    users,
    isLoadingUsers,
    usersByUserName,
    refreshUsers,
    getUserByUserName,
    updateUser,
    addUser,
    removeUser
  }
})
